// @ts-expect-error
import localStorage from "localStorage"; // @ts-ignore
import { createContext, useContext, useEffect, useState } from "react";
import apiClient from "../core/http";
import { trackEvent } from "../shared/analytics";
import {
  generateRedditEventId,
  getRedditClickId,
  trackRedditEvent,
} from "../shared/reddit";

//Create the Auth Context with the data type specified
//and a empty object
const AuthContext = createContext({});

export type AuthData = {
  token: string;
  id: number;
  firstName: string;
  lastName: string;
  email: string;
  profilePicture: string;
  premiumLifetime: boolean;
  premium: boolean;
  betaUser: boolean;
  role: number;
  userHandle: string | undefined;
  partnerId: number | undefined;
  partnerName: string | undefined;
};

const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [authData, setAuthData] = useState<AuthData | undefined>(undefined);

  //the AuthContext start with loading equals true
  //and stay like this, until the data be load from Async Storage
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    //Every time the App is opened, this provider is rendered
    //and call de loadStorage function.
    loadStorageData();
  }, []);

  async function loadStorageData() {
    try {
      //Try get the data from Async Storage
      const authDataSerialized = localStorage.getItem("@AuthData");
      if (authDataSerialized) {
        //If there are data, it's converted to an Object and the state is updated.
        const _authData = JSON.parse(authDataSerialized);
        setAuthData(_authData);
      }
    } catch (error) {
    } finally {
      //loading finished
      setLoading(false);
    }
  }

  const signIn = async (email: string, password: string) => {
    //call the service passing credential (email and password).
    //In a real App this data will be provided by the user from some InputText components.
    const deviceName = "web";
    return apiClient
      .post("/sanctum/token", {
        email,
        password,
        device_name: deviceName,
      })
      .then((response) => {
        const token = response.data;
        return apiClient
          .get("/user", {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(({ data }) => {
            const _authData = {
              token,
              ...data.data,
            };
            //Set the data in the context, so the App can be notified
            //and send the user to the AuthStack
            setAuthData(_authData);

            //Persist the data in the Async Storage
            //to be recovered in the next user session.
            localStorage.setItem("@AuthData", JSON.stringify(_authData));
          });
      });
  };

  const socialSignIn = async (
    provider: string,
    accessToken: string,
    deviceName: string,
    payload: string | null = null
  ) => {
    //call the service passing credential (email and password).
    //In a real App this data will be provided by the user from some InputText components.
    return apiClient
      .post("/social-login", {
        provider,
        access_token: accessToken,
        device_name: deviceName,
        platform: "web",
        payload,
      })
      .then((response) => {
        const token = response.data;
        return apiClient
          .get("/user", {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(({ data }) => {
            const _authData = {
              token,
              ...data.data,
            };
            //Set the data in the context, so the App can be notified
            //and send the user to the AuthStack
            setAuthData(_authData);

            //Persist the data in the Async Storage
            //to be recovered in the next user session.
            localStorage.setItem("@AuthData", JSON.stringify(_authData));

            return {
              token,
              ...data.data,
            };
          })
          .catch((err) => {
            console.log(err);
          });
      })
      .catch((err) => {
        console.log(err);
      });
  };

  /**
   * Reset password
   * @param {*} email
   * @param {*} password
   * @returns
   */
  const resetPassword = async (email: string) => {
    return apiClient
      .post("/reset-password", { email })
      .then((response) => {
        trackEvent("password_reset_requested");
        return response.data;
      })
      .catch((err) => {
        console.log(err);
        return false;
      });
  };

  /**
   * Sign up a new user
   * @param {*} firstName
   * @param {*} lastName
   * @param {*} email
   * @param {*} password
   * @param {*} userHandle
   * @returns
   */
  const signUp = async (
    firstName: string,
    lastName: string,
    email: string,
    password: string,
    userHandle: string
  ) => {
    const deviceName = "web";
    const redditEventId = generateRedditEventId();
    return apiClient
      .post("/signup", {
        firstName,
        lastName,
        email,
        password,
        userHandle,
        device_name: deviceName,
        redditClickId: getRedditClickId(),
        redditEventId,
      })
      .then((response) => {
        const token = response.data;
        return apiClient
          .get("/user", {
            headers: { Authorization: `Bearer ${token}` },
          })
          .then(({ data }) => {
            const _authData = {
              token,
              ...data.data,
            };
            setAuthData(_authData);
            localStorage.setItem("@AuthData", JSON.stringify(_authData));
            trackRedditEvent("SignUp", {}, redditEventId);

            return {
              token,
              ...data.data,
            };
          });
      });
  };

  const signOut = async () => {
    trackEvent("logout");
    //Remove data from context, so the App can be notified
    //and send the user to the AuthStack
    setAuthData(undefined);

    //Remove the data from Async Storage
    //to NOT be recoverede in next session.
    await localStorage.removeItem("@AuthData");
  };

  const updateUser = async (userData: AuthData) => {
    const _authData: AuthData = {
      ...authData,
      ...userData,
    };
    setAuthData(_authData);
    await localStorage.setItem("@AuthData", JSON.stringify(_authData));
  };

  return (
    //This component will be used to encapsulate the whole App,
    //so all components will have access to the Context
    <AuthContext.Provider
      value={{
        authData,
        loading,
        signIn,
        signOut,
        updateUser,
        socialSignIn,
        signUp,
        resetPassword,
      }}
    >
      {loading ? " " : children}
    </AuthContext.Provider>
  );
};

//A simple hooks to facilitate the access to the AuthContext
// and permit components to subscribe to AuthContext updates
function useAuth() {
  const context = useContext(AuthContext);

  if (!context) {
    throw new Error("useAuth must be used within an AuthProvider");
  }

  return context;
}

export { AuthContext, AuthProvider, useAuth };
